import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "body",
    "submitButton",
    "error",
    "topic",
    "fileInput",
    "captureButton",
    "preview",
    "previewImage",
    "previewName",
    "formPanel",
    "successPanel"
  ]
  static values = {
    kind: String,
    url: String
  }

  connect() {
    this.selectedTopics = new Set()
    this.element.addEventListener("hidden.bs.modal", this.reset)
    this.refreshSubmit()
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this.reset)
    this.revokePreview()
  }

  reset = () => {
    this.selectedTopics = new Set()
    this.topicTargets.forEach((button) => {
      button.classList.remove("is-selected")
      button.setAttribute("aria-pressed", "false")
    })
    if (this.hasBodyTarget) this.bodyTarget.value = ""
    this.removeScreenshot()
    this.hideError()
    this.showForm()
    this.refreshSubmit()
  }

  toggleTopic(event) {
    const topic = event.params.topic
    const button = event.currentTarget
    if (this.selectedTopics.has(topic)) {
      this.selectedTopics.delete(topic)
      button.classList.remove("is-selected")
      button.setAttribute("aria-pressed", "false")
    } else {
      this.selectedTopics.add(topic)
      button.classList.add("is-selected")
      button.setAttribute("aria-pressed", "true")
    }
  }

  pickScreenshot() {
    this.fileInputTarget.click()
  }

  screenshotChosen() {
    const file = this.fileInputTarget.files[0]
    if (!file) {
      this.removeScreenshot()
      return
    }

    this.revokePreview()
    this.previewUrl = URL.createObjectURL(file)
    this.previewImageTarget.src = this.previewUrl
    this.previewNameTarget.textContent = file.name
    this.previewTarget.hidden = false
    this.captureButtonTarget.hidden = true
  }

  removeScreenshot() {
    this.revokePreview()
    this.fileInputTarget.value = ""
    if (this.hasPreviewTarget) this.previewTarget.hidden = true
    if (this.hasCaptureButtonTarget) this.captureButtonTarget.hidden = false
  }

  refreshSubmit() {
    if (!this.hasSubmitButtonTarget) return
    this.submitButtonTarget.disabled = this.bodyTarget.value.trim().length === 0
  }

  async submit() {
    this.hideError()
    const message = this.bodyTarget.value.trim()
    if (message.length === 0) {
      this.showError("Please write a message.")
      return
    }

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = "Sending…"

    const formData = new FormData()
    formData.append("kind", this.kindValue)
    formData.append("body", message)
    formData.append("page_url", window.location.href)
    this.selectedTopics.forEach((topic) => formData.append("topics[]", topic))
    const file = this.fileInputTarget.files[0]
    if (file) formData.append("screenshot", file)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          Accept: "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json().catch(() => ({}))
      if (response.ok && data.ok) {
        this.showSuccess()
        return
      }

      this.showError(data.error || "We couldn't send that. Please try again.")
    } catch (_error) {
      this.showError("We couldn't send that. Please try again.")
    } finally {
      this.submitButtonTarget.textContent = this.kindValue === "help" ? "Send" : "Submit Feedback"
      this.refreshSubmit()
    }
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  hideError() {
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }

  showSuccess() {
    this.formPanelTarget.hidden = true
    this.successPanelTarget.hidden = false
  }

  showForm() {
    this.formPanelTarget.hidden = false
    this.successPanelTarget.hidden = true
  }

  revokePreview() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = null
    }
    if (this.hasPreviewImageTarget) this.previewImageTarget.removeAttribute("src")
  }
}
